import { useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import Checkbox from 'monday-ui-react-core/dist/Checkbox';
import TextField from 'monday-ui-react-core/dist/TextField';
import Dropdown from 'monday-ui-react-core/dist/Dropdown';
import Button from 'monday-ui-react-core/dist/Button';
import CustomColorPicker from '../../CustomColorPicker/CustomColorPicker';
import useLineItemSettings from '../Hooks/useLineItemSettings';
import styled from 'styled-components';
import CloseIcon from '../../../assets/icons/close.svg';
import { FileContext } from '../../../context/FileContext';
import useLineItemState from '../Hooks/useLineItems';

import SubitemFilter from '../../SubItemFilters';
import { Controller, FormProvider } from 'react-hook-form';

const Header = styled.h2`
  color: ${({ color }) => color};
`;

const Labels = styled.p`
  padding: var(--spacing-xs) 0;
  font-size: 14px;
  color: #323338;
`;

const ControllerSettingsContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  background: var(--light-primary-background-color, #fff);
  padding: 18px 14px 18px 14px;

  .text {
    color: var(--light-primary-text-color, #323338);
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .icon {
      cursor: pointer;
    }
  }

  .content {
    margin: 20px 0 20px 0;
    display: flex;
    gap: 30px;
    @media only screen and (max-width: 768px) {
      flex-direction: column;
    }

    .col {
      width: 25%;
      display: flex;
      flex-direction: column;
      gap: 30px;
      @media only screen and (max-width: 768px) {
        width: 100%;
      }

      .item {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .mt-xs {
          margin: var(--spacing-xs) 0;
        }
      }
    }

    .col.no-col-gap {
      gap: 10px;
    }
  }

  .hl {
    height: 1px;
    width: 100%;
    background-color: var(--ui-border-color);
  }

  .subitem-filter-container {
    padding: var(--spacing-medium) 0;

    position: relative;
    height: 200px;
    overflow-y: scroll;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #676879;
      border-radius: 1px;
      height: 25px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgb(148, 149, 159);
    }

    &::-webkit-scrollbar-track {
      background: #f0f0f0;
    }
  }

  .actions {
    text-align: right;

    .button {
      width: 48px;
    }
  }
`;

const SelectColumnsContainer = styled.div`
  height: max-height;
  overflow-y: scroll;
  /* Ensure scrollbar is visible and styled */
  &::-webkit-scrollbar {
    width: 4px; /* Adjust width */
  }

  &::-webkit-scrollbar-thumb {
    background: #676879; /* Scrollbar color */
    border-radius: 1px;
    height: 25px; /* Adjust thumb height */
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgb(148, 149, 159); /* Slightly darker on hover */
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0; /* Track color to make it stand out */
  }
`;

const ControllerSettings = ({ t, render }) => {
  const {
    itemId,
    addedFormFields,
    currentSelectedItem,
    updateFormFields,
    setShowLineItemModal,
    triggerToast,
    subItems,
  } = useContext(FileContext);

  const queryClient = useQueryClient();

  const queryData = queryClient.getQueryData(['sub-items', itemId]);
  const selectedItem = addedFormFields[currentSelectedItem.current];

  const selectedItemId = selectedItem?.id;
  const { columnOptions, setDefaultSubItems } = useLineItemSettings({
    selectedItem,
  });

  const {
    formData,
    currencyPositionOptions,
    currencyTypesOptions,
    taxTypeOptions,
    rgbMapper,

    updateFormData,
    validateFormData,
    tableHeaderRef,
    updateFilters,
    filterFormMethods,
  } = useLineItemState({ selectedItem, triggerToast });

  const filterConfig = filterFormMethods.getValues();

  const calculateTableDimenisions = () => {
    let columnWidth = {};
    const totalTableWidth = tableHeaderRef.current.clientWidth;
    if (tableHeaderRef.current) {
      const thElements = tableHeaderRef.current.querySelectorAll('th');
      thElements.forEach((th, index) => {
        const percent = (th.clientWidth / totalTableWidth) * 100;
        if (formData.itemNameColumn?.checked) {
          if (index === 0) {
            columnWidth = { ...columnWidth, 'item-name': percent };
          } else {
            const col = formData.columns?.[index - 1];
            if (col?.id) {
              columnWidth = { ...columnWidth, [col.id]: percent };
            }
          }
        } else {
          const col = formData.columns?.[index];
          if (col?.id) {
            columnWidth = { ...columnWidth, [col.id]: percent };
          }
        }
      });
    }
    updateFormData('columnWidth', columnWidth);
    return columnWidth;
  };

  const handleSaveButtonClick = async () => {
    const columnWidth = calculateTableDimenisions();

    filterFormMethods.handleSubmit(
      (data) => {
        if (validateFormData()) {
          const updatedFormData = {
            ...formData,
            filterConfig: { ...data },
            columnWidth: columnWidth,
          };

          selectedItem.subItemSettings = updatedFormData;

          updateFormFields([...addedFormFields]);
          handleModalClose();
        }
      },
      (error) => console.log({ error }),
    )();
  };

  const handleModalClose = () => {
    return setShowLineItemModal(false);
  };

  useEffect(() => {
    if (queryData?.data?.items?.[0].subitems?.length) {
      setDefaultSubItems(queryData?.data?.items?.[0]?.subitems);
    }
  }, [queryData, setDefaultSubItems]);

  return (
    <>
      <ControllerSettingsContainer>
        <div className="header">
          <Header>{t('Configure sub items')}</Header>
          <img
            className="icon"
            src={CloseIcon}
            alt="close"
            onClick={handleModalClose}
          />
        </div>
        <div className="content">
          <div className="col">
            <div className="item">
              <Checkbox
                label={t('Show Sum at the bottom')}
                checked={formData.sum.checked}
                onChange={() =>
                  updateFormData('sum.checked', !formData.sum.checked)
                }
              />
              <TextField
                placeholder={t('Sum')}
                value={formData.sum.label}
                disabled={!formData.sum.checked}
                onChange={(value) => updateFormData('sum.label', value)}
              />
              <Dropdown
                placeholder={t('Set Column to Sum')}
                options={columnOptions
                  .filter((x) =>
                    ['numeric', 'numbers', 'formula'].includes(x.type),
                  )
                  .filter((y) =>
                    formData.columns.map((x) => x.id).includes(y.id),
                  )}
                size={Dropdown.size.SMALL}
                value={formData.sum.column}
                disabled={!formData.sum.checked}
                onChange={(value) => {
                  updateFormData('sum.column', value);
                }}
              />
            </div>
            <div className="item">
              <Checkbox
                label={t('Change header background')}
                checked={formData.header.checked}
                onChange={() =>
                  updateFormData('header.checked', !formData.header.checked)
                }
              />
              <CustomColorPicker
                value={formData.header.color}
                disabled={!formData.header.checked}
                handleInput={(val) => {
                  updateFormData('header.color', val.toString());
                  updateFormData('header.rgb', rgbMapper[val.toString()]);
                }}
              />
            </div>
          </div>
          <div className="col">
            <div className="item">
              <Checkbox
                label={t('Show Tax at the bottom')}
                checked={formData.tax.checked}
                onChange={() =>
                  updateFormData('tax.checked', !formData.tax.checked)
                }
              />
              <TextField
                placeholder={t('Tax')}
                value={formData.tax.label}
                disabled={!formData.tax.checked}
                onChange={(value) => updateFormData('tax.label', value)}
              />
              <Dropdown
                placeholder={t('Type')}
                options={taxTypeOptions}
                size={Dropdown.size.SMALL}
                value={formData.tax.type}
                disabled={!formData.tax.checked}
                onChange={(value) => updateFormData('tax.type', value)}
              />
              <TextField
                placeholder={t('Value')}
                value={formData.tax.value}
                disabled={!formData.tax.checked}
                type="number"
                onChange={(value) => updateFormData('tax.value', value)}
              />
            </div>
            <div className="item">
              <Checkbox
                label={t('Customize Unit')}
                checked={formData.currency.checked}
                onChange={() =>
                  updateFormData('currency.checked', !formData.currency.checked)
                }
              />
              <Dropdown
                placeholder={t('Select Currency')}
                options={currencyTypesOptions}
                size={Dropdown.size.SMALL}
                value={formData.currency.type}
                disabled={!formData.currency.checked}
                onChange={(value) => updateFormData('currency.type', value)}
              />
              <Dropdown
                placeholder={t('Position')}
                options={currencyPositionOptions}
                size={Dropdown.size.SMALL}
                value={formData.currency.position}
                disabled={!formData.currency.checked}
                onChange={(value) => updateFormData('currency.position', value)}
              />
              <Dropdown
                placeholder={t('Show in Column')}
                multiline
                options={
                  columnOptions?.map((x) => ({
                    value: x.id,
                    label: x.title,
                  }))
                  // .filter((x) => ['formula'].includes(x.type))
                  // .filter((y) =>
                  //   formData.columns.map((x) => x.id).includes(y.id),
                  // )
                }
                size={Dropdown.size.SMALL}
                value={formData.currency.column || []}
                disabled={!formData.currency.checked}
                multi
                onOptionRemove={(value) => {
                  // TO HANDLE OPTION REMOVE
                  const fieldValue = formData.currency.column.filter(
                    (x) => x.value !== value.value,
                  );

                  updateFormData('currency.column', fieldValue);
                }}
                onOptionSelect={(value) => {
                  // TO HANDLE OPTION SELECT
                  updateFormData(
                    'currency.column',
                    [...formData.currency.column, value] || [],
                  );
                }}
                onChange={(value) => {
                  // TO HANDLE CLEARING THE DROPDOWN
                  if (!value) {
                    updateFormData('currency.column', []);
                  }
                }}
              />
            </div>
          </div>
          <div className="col no-col-gap">
            <div className="item">
              <div className="text">{t('Select Columns')}</div>
              <SelectColumnsContainer>
                <Checkbox
                  className="mt-xs"
                  label={formData.itemNameColumn?.label ?? t('Item')}
                  checked={!!formData.itemNameColumn?.checked}
                  onChange={(e) => {
                    updateFormData('itemNameColumn.checked', e.target.checked);
                  }}
                />

                {columnOptions?.map((opt) => (
                  <Checkbox
                    className="mt-xs"
                    label={opt.label || ''}
                    key={opt.id}
                    checked={!!formData.columns.find((x) => x.id === opt.id)}
                    onChange={(e) => {
                      if (formData.columns.length > 5 && e.target.checked) {
                        triggerToast(
                          t('You cannot add more than six columns.'),
                        );
                        return;
                      }
                      if (formData.sum.column === opt) {
                        updateFormData('sum.column', null);
                        triggerToast(
                          `${t(
                            'The sum column has been cleared because the',
                          )} ${opt.label} ${t('is removed from column')}`,
                        );
                      }
                      updateFormData('columns', {
                        ...opt,
                        checked: e.target.checked,
                      });
                    }}
                  />
                ))}
              </SelectColumnsContainer>
            </div>
          </div>
        </div>

        <div className="hl"></div>
        <FormProvider {...filterFormMethods}>
          <div className="subitem-filter-container">
            <Controller
              control={filterFormMethods.control}
              name="isFilterEnabled"
              render={({ field }) => (
                <Checkbox
                  label={t('Filter subitems')}
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    updateFormData(
                      'filterConfig.isFilterEnabled',
                      !Boolean(formData.filterConfig.isFilterEnabled),
                    );
                  }}
                />
              )}
            />

            <Labels>
              Filter subitems appearing in your document based on the following
              conditions:
            </Labels>

            <SubitemFilter
              updateFilters={updateFilters}
              filterConfig={formData.filterConfig}
              updateFormData={updateFormData}
            />
          </div>
        </FormProvider>

        <div className="actions">
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.PRIMARY}
            className="button"
            onClick={handleSaveButtonClick}
          >
            {t('Save')}
          </Button>
        </div>
      </ControllerSettingsContainer>
      {render(
        { ...formData, filterConfig: { ...filterConfig } },
        tableHeaderRef,
        subItems,
        selectedItemId,
      )}
    </>
  );
};

export default ControllerSettings;
